/**
 * Signup.js — Invite-based account creation
 * User arrives via: /signup?token=abc123
 */

import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

const API_BASE = process.env.NODE_ENV === "production" ? "/api/v1" : "http://127.0.0.1:8000/api/v1";

const C = {
  bg: "#0a0f1a", card: "#1a2332", border: "#2a3a4e",
  text: "#e2e8f0", muted: "#94a3b8", accent: "#3b82f6",
  green: "#22c55e", red: "#ef4444",
};

const inputStyle = {
  width: "100%", padding: "12px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
  background: "#0b1120", color: C.text, fontSize: 15, outline: "none", boxSizing: "border-box",
};

export default function Signup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("No invite token provided. Please use the link from your invitation email.");
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/auth/invite/validate/?token=${token}`)
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (ok && data.valid) setInvite(data);
        else setError(data.error || "Invalid invite link");
      })
      .catch(() => setError("Unable to connect to server"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    setSubmitError("");
    if (!username.trim()) { setSubmitError("Username is required"); return; }
    if (username.length < 3) { setSubmitError("Username must be at least 3 characters"); return; }
    if (!password) { setSubmitError("Password is required"); return; }
    if (password.length < 8) { setSubmitError("Password must be at least 8 characters"); return; }
    if (password !== confirmPw) { setSubmitError("Passwords do not match"); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/invite/accept/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, username: username.trim(), password }),
      });
      const data = await res.json();
      if (res.ok && data.success) setSuccess(true);
      else {
        const err = data.error;
        setSubmitError(Array.isArray(err) ? err.join(", ") : (err || "Account creation failed"));
      }
    } catch { setSubmitError("Unable to connect to server"); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: 440, background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}>
        <div style={{ padding: "28px 32px 20px", borderBottom: `1px solid ${C.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 4 }}>Helena Partners</div>
          <div style={{ fontSize: 12, color: C.muted, letterSpacing: "0.05em" }}>PORTFOLIO MANAGEMENT SYSTEM</div>
        </div>
        <div style={{ padding: "28px 32px 32px" }}>
          {loading && <div style={{ textAlign: "center", color: C.muted, padding: 40 }}>Validating invite...</div>}

          {!loading && error && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🚫</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: C.red, marginBottom: 8 }}>{error}</div>
              <div style={{ fontSize: 13, color: C.muted }}>Contact your administrator for a new invite.</div>
            </div>
          )}

          {success && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.green, marginBottom: 8 }}>Account Created</div>
              <div style={{ fontSize: 14, color: C.muted, marginBottom: 20 }}>You can now log in with your username and password.</div>
              <button onClick={() => navigate("/login")} style={{ padding: "12px 32px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Go to Login</button>
            </div>
          )}

          {!loading && !error && !success && invite && (
            <>
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <div style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 4 }}>Welcome, {invite.first_name || "there"}!</div>
                <div style={{ fontSize: 13, color: C.muted }}>Create your account to get started</div>
              </div>
              {submitError && <div style={{ padding: "10px 14px", borderRadius: 6, background: "rgba(239,68,68,.12)", border: `1px solid ${C.red}33`, color: C.red, fontSize: 13, marginBottom: 16 }}>{submitError}</div>}
              <div style={{ background: "#0b1120", borderRadius: 8, padding: 14, marginBottom: 20, border: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                  <span style={{ color: C.muted, fontSize: 13 }}>Name</span>
                  <span style={{ color: C.text, fontSize: 13, fontWeight: 500 }}>{invite.first_name} {invite.last_name}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                  <span style={{ color: C.muted, fontSize: 13 }}>Email</span>
                  <span style={{ color: C.accent, fontSize: 13 }}>{invite.email}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                  <span style={{ color: C.muted, fontSize: 13 }}>Role</span>
                  <span style={{ color: C.text, fontSize: 13, fontWeight: 500 }}>{invite.role}</span>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, textTransform: "uppercase" }}>Choose a Username</label>
                <input style={inputStyle} value={username} onChange={e => setUsername(e.target.value)} placeholder="e.g. jeremy.weech" autoComplete="username" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, textTransform: "uppercase" }}>Create Password</label>
                <input style={inputStyle} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimum 8 characters" autoComplete="new-password" />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, textTransform: "uppercase" }}>Confirm Password</label>
                <input style={inputStyle} type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Type it again" autoComplete="new-password" />
              </div>
              <button onClick={handleSubmit} disabled={submitting} style={{ width: "100%", padding: "14px", borderRadius: 8, border: "none", background: submitting ? C.muted : C.accent, color: "#fff", fontSize: 15, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer" }}>
                {submitting ? "Creating Account..." : "Create Account"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
